import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { AlertSeverity } from '@src/suite/common/enums/enums';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { selectCollaborativeEditingAPIKey } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { ProductionCommandType } from '@src/suite/common/utils/ml-2-html-utils';
import { isStoryInRundown } from '@src/suite/common/utils/record-utils';

import { StoryBasicTextEditorComponent } from './st-story-basic-text-editor/st-story-basic-text-editor';
import { StoryCollaborationTextEditorComponent } from './st-story-collaboration-text-editor/st-story-collaboration-text-editor';
import { StStoryToolbarComponent } from './st-story-toolbar/st-story-toolbar';

export enum BlackLinePosition {
    TOP = 'top',
    MIDDLE = 'middle',
    BOTTOM = 'bottom',
}

@Component({
    selector: 'st-story-editor',
    imports: [MatIcon, NgTemplateOutlet, StoryBasicTextEditorComponent, StoryCollaborationTextEditorComponent, StStoryToolbarComponent],
    templateUrl: './st-story-editor.html',
    styleUrl: './st-story-editor.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-editor',
    },
})
export class StoryEditorComponent {
    private readonly store = inject(Store);
    private readonly mosEventService = inject(MosEventService);

    readonly selectedTextChange = output<string>();

    constructor() {
        // Route MOS add/replace events to the currently focused child editor.
        // The storyGuid check ensures we only handle events targeted at this story.
        this.mosEventService
            .onMosItem()
            .pipe(takeUntilDestroyed())
            .subscribe(({ storyGuid, mosCommand }) => {
                if (storyGuid !== this.story()?.Guid) return;
                this.textEditor()?.addMosItem(mosCommand);
            });

        this.mosEventService
            .onMosItemEdit()
            .pipe(takeUntilDestroyed())
            .subscribe(({ storyGuid, mosCommand, mosItemId: mosElId }) => {
                if (storyGuid !== this.story()?.Guid) return;
                this.textEditor()?.editMosItem(mosCommand, mosElId);
            });

        effect(() => {
            this.mosEventService.setCurrentMirStatus({ ...this.currentStoryEditorComponent()?.resolveCurrentMirItemId(this.currentEditorInstance()), storyId: this.story()?.Guid });
        });

        effect(() => {
            this.codoxInitializing.emit(this.isCodoxInitializing());
        });
    }

    // Inputs
    readonly story = input<StorytellingStoryRecord | null>(null);
    readonly disabled = input(false, { transform: coerceBooleanProperty });
    readonly hasUnsavedChanges = input(false, { transform: coerceBooleanProperty });

    /**
     * When true, the editor is editable as long as the story is locked by us, or if it is in collaboration mode it is always true.
     * In summary:
     *  - In collaboration mode: isEditableByLock is always true, the editor is always editable as long as the story is not in RO.
     *  - In non-collaboration mode: isEditableByLock is true when the story is locked by us, that means we are in editing mode, and false when the story is unlocked,
     * that means we are not in editing mode, even if the story is not in RO, to avoid losing cursor position on every content change.
     */
    readonly isEditableByLock = input(false, { transform: coerceBooleanProperty });

    // Outputs
    // contentChanged: emitted on every editor change (debounced ~300 ms in collaboration mode
    //   via the Codox hook). Used by the parent solely to track the latest content locally —
    //   it does NOT trigger a store update on its own.
    // save: emitted when the user explicitly saves (toolbar save button / Ctrl+S).
    //   Always carries fresh content read directly from the live Froala instance.
    readonly contentChanged = output<{ aboveBlackLineText: string; belowBlackLineText: string }>();
    readonly save = output<{ aboveBlackLineText: string; belowBlackLineText: string }>();
    readonly collabUsers = output<{ id: string }[]>();
    readonly lockStory = output<boolean>();
    readonly mosItemOpen = output<MosEditorEvent>();
    readonly mosMediaClicked = output<string>();
    readonly approvedChanged = output<boolean>();
    // True while at least one collaboration editor is waiting for Codox to connect.
    readonly codoxInitializing = output<boolean>();

    // Tracks the per-editor Codox initialisation state so the combined output can be derived.
    // Not private: the template sets these directly from (codoxInitializing) bindings.
    readonly aboveCodoxInitializing = signal(false);
    readonly belowCodoxInitializing = signal(false);
    private readonly isCodoxInitializing = computed(() => this.aboveCodoxInitializing() || this.belowCodoxInitializing());

    // View children used to read live Froala content synchronously at save time.
    // Only one pair is ever present in the DOM at a time (collab OR basic, never both).
    private readonly aboveCollabEditor = viewChild<StoryCollaborationTextEditorComponent>('aboveCollabEditor');
    private readonly belowCollabEditor = viewChild<StoryCollaborationTextEditorComponent>('belowCollabEditor');
    private readonly aboveBasicEditor = viewChild<StoryBasicTextEditorComponent>('aboveBasicEditor');
    private readonly belowBasicEditor = viewChild<StoryBasicTextEditorComponent>('belowBasicEditor');

    // Computed
    private readonly codoxApiKey = computed(() => this.store.selectSignal(selectCollaborativeEditingAPIKey)());
    private readonly isStoryInRO = computed(() => isStoryInRundown(this.story()));
    readonly textEditor = computed(() => {
        if (this.lastFocusedEditor() === 'above') {
            return this.aboveCollabEditor() ?? this.aboveBasicEditor();
        } else {
            return this.belowCollabEditor() ?? this.belowBasicEditor();
        }
    });
    readonly textSelection = computed(() => this.textEditor()?.editor().selection.get() as Selection);

    readonly collaborationEnabled = computed(() => {
        const story = this.story();
        return !!story?.isWriteable && !!this.codoxApiKey() && !story?.isNewRecord && !this.isStoryInRO();
    });

    readonly aboveDocId = computed(() => {
        if (!this.collaborationEnabled()) {
            return null;
        }
        const guid = this.story()?.Guid;
        return guid ? `${guid}_above` : '';
    });

    readonly belowDocId = computed(() => {
        if (!this.collaborationEnabled()) {
            return null;
        }
        const guid = this.story()?.Guid;
        return guid ? `${guid}_below` : '';
    });

    // aboveText / belowText: the story's persisted text bound to [text] on child editors.
    //
    // The computation only resets the value when a genuinely new story is loaded (guid changes
    // AND content is marked as loaded) AND the editor is in an editable state (component
    // disabled — read-only view — or story locked by us for editing). Otherwise the previous
    // computed value is preserved so in-progress edits and cursor position are not lost on
    // every store update (e.g. RTU, save round-trip).
    //
    // The previous source snapshot (prev.source.guid) replaces the old currentStoryGuid signal
    // — the linkedSignal framework tracks it internally.
    //
    // !IMPORTANT: Do NOT change this logic without fully understanding the implications on
    // cursor position and content stability. This is the single source of truth for when
    // aboveText / belowText are reset from the store.
    private readonly storyTextSource = () => ({
        guid: this.story()?.loadedBasicContent ? (this.story()?.Guid ?? null) : null,
        loaded: this.story()?.loadedBasicContent ?? false,
        above: this.story()?.aboveBlackLineText ?? '',
        below: this.story()?.belowBlackLineText ?? '',
        editable: !this.disabled() && this.isEditableByLock(),
    });

    /**
     * !IMPORTANT NOTE ON ABOVE/BELOW TEXT UPDATING LOGIC
     *
     * This effect is responsible for updating aboveText and belowText from the story input when a new story is loaded or when the story is unlocked for editing,
     * depending on the mode we are in, collaboration or non-collaboration, to avoid resetting the Froala editor's internal state and losing the cursor position on every content change.
     * DON'T change this logic without fully understanding the implications on the editor's behavior, especially regarding cursor position and content updating.
     * THIS EFFECT AND THE currentStoryGuid SIGNAL, and should be responsible only for those updates.
     * It should remain the single source of truth for when aboveText and belowText are updated from the story input, to ensure consistent behavior across all modes and states of the editor.
     *
     * In disabled for editing mode (isWriteable = false) we want to always update aboveText and belowText from the story input,
     * because the user can't make changes to the content and we want to reflect any changes in the story input immediately in the editor, regardless of story.Guid or story.isWriteable.
     *
     * When we are in collaboration mode, we want to update aboveText and belowText only when a new story is loaded (identified by a change in story.Guid), not on every store update, and we don't care RTU or the lock of the story.
     * This way, we avoid resetting the Froala editor's internal state and losing the cursor position on every content change.
     *
     * When not in collaboration mode, we need to stop listening to changes in the story input when the story is locked by us, that means we are in editing mode,
     * to avoid updating aboveText and belowText from the story input and losing the cursor position on every content change,
     * and we want to update aboveText and belowText when a new story is loaded (identified by a change in story.Guid)
     * or when the story is unlocked by us (identified by a change in story.isWriteable from false to true).
     *
     * when story is disabled for editing (isWriteable = false) we want to update aboveText and belowText always from the story input,
     * because the user can't make changes to the content and we want to reflect any changes in the story input immediately in the editor.
     *
     * In summary:
     * - In collaboration mode: update aboveText/belowText only when story.Guid changes.
     * - In non-collaboration mode: update aboveText/belowText when story.Guid changes or when story.isWriteable is false and changes to true.
     * Otherswise, do not update aboveText/belowText to avoid resetting the Froala editor's internal state and losing the cursor position on every content change.
     * - In disabled for editing mode: always update aboveText/belowText from story input.
     *
     */
    readonly aboveText = linkedSignal({
        source: this.storyTextSource,
        computation: (curr, prev): string => {
            if (!curr.loaded) {
                return prev?.value ?? '';
            }
            const guidChanged = curr.guid !== (prev?.source?.guid ?? '\0');
            // In disabled for editing mode, we want to always update aboveText and belowText from the story input,
            // because the user can't make changes to the content and we want to reflect any changes in the story input immediately in the editor, regardless of story.Guid or story.isWriteable.
            if (guidChanged || !curr.editable) {
                return curr.above;
            }
            return prev?.value ?? curr.above;
        },
    });

    readonly belowText = linkedSignal({
        source: this.storyTextSource,
        computation: (curr, prev): string => {
            if (!curr.loaded) return prev?.value ?? '';
            const guidChanged = curr.guid !== (prev?.source?.guid ?? '\0');
            // In disabled for editing mode, we want to always update aboveText and belowText from the story input,
            // because the user can't make changes to the content and we want to reflect any changes in the story input immediately in the editor, regardless of story.Guid or story.isWriteable.
            if (guidChanged || !curr.editable) {
                return curr.below;
            }
            return prev?.value ?? curr.below;
        },
    });

    // i18n for toolbar production command tooltips
    readonly i18n = computed(() => this.store.selectSignal(selectI18N)());

    readonly editorComponentAboveBL = computed(() => this.aboveCollabEditor() ?? this.aboveBasicEditor() ?? null);
    readonly editorComponentBelowBL = computed(() => this.belowCollabEditor() ?? this.belowBasicEditor() ?? null);

    readonly editorAboveBL = computed(() => this.editorComponentAboveBL()?.editor() ?? null);
    readonly editorBelowBL = computed(() => this.editorComponentBelowBL()?.editor() ?? null);

    // The Froala instance of the last-focused editor — passed to the toolbar so its
    // formatting commands and active-state queries target the right editor.
    readonly currentStoryEditorComponent = computed(() => {
        if (this.lastFocusedEditor() === 'below') {
            return this.editorComponentBelowBL();
        }
        return this.editorComponentAboveBL();
    });

    readonly currentEditorInstance = computed(() => {
        const ref = this.currentStoryEditorComponent();
        return ref?.editor() ?? null;
    });

    // aboveContent / belowContent: local mirror of the latest typed content.
    // Updated by onAboveChanged / onBelowChanged on every editor change. Initialized from
    // (and automatically reset to) aboveText / belowText whenever the story input changes,
    // thanks to linkedSignal. Used as a fallback in getCurrentContent() when the Froala
    // instance is unavailable (e.g. not yet initialized).
    readonly aboveContent = linkedSignal(() => {
        return this.aboveText();
    });
    readonly belowContent = linkedSignal(() => this.belowText());

    // Updates the local content mirror and bubbles the change to the parent.
    // Intentionally does NOT feed back into [text] on the child editors — see aboveText above.
    onAboveChanged(text: string): void {
        this.aboveContent.set(text);
        this.contentChanged.emit({ aboveBlackLineText: this.aboveContent(), belowBlackLineText: this.belowContent() });
    }

    onBelowChanged(text: string): void {
        this.belowContent.set(text);
        this.contentChanged.emit({ aboveBlackLineText: this.aboveContent(), belowBlackLineText: this.belowContent() });
    }

    // Reads live HTML directly from the Froala instances for the most accurate content.
    // Falls back to the locally tracked signals if the editor is not yet initialized.
    // Called by onSave() and can be called externally when synchronous live content is needed.
    getCurrentContent(): { aboveBlackLineText: string; belowBlackLineText: string } {
        const aboveEditor = this.editorComponentAboveBL();
        const belowEditor = this.editorComponentBelowBL();
        return {
            aboveBlackLineText: aboveEditor?.editor()?.html.get() ?? this.aboveContent(),
            belowBlackLineText: belowEditor?.editor()?.html.get() ?? this.belowContent(),
        };
    }

    // Tracks which editor the user last focused, so cursor save/restore targets the right one.
    // Updated via (focusin) bindings in the template. Signal so currentEditorInstance recomputes
    // reactively when focus switches between editors.
    readonly lastFocusedEditor = signal<'above' | 'below'>('above');

    // Black-line resize: tracks one of three positions for the divider.
    // BOTTOM = above editor dominant (default), MIDDLE = 50/50, TOP = below editor dominant.
    readonly BlackLinePosition = BlackLinePosition;
    readonly currentPosition = signal<BlackLinePosition>(BlackLinePosition.BOTTOM);
    readonly hasContentBelow = computed(() => !!this.belowContent().trim());

    onArrowUpClick(): void {
        if (this.currentPosition() === BlackLinePosition.BOTTOM) {
            this.currentPosition.set(BlackLinePosition.MIDDLE);
        } else if (this.currentPosition() === BlackLinePosition.MIDDLE) {
            this.currentPosition.set(BlackLinePosition.TOP);
        }
    }

    onArrowDownClick(): void {
        if (this.currentPosition() === BlackLinePosition.TOP) {
            this.currentPosition.set(BlackLinePosition.MIDDLE);
        } else if (this.currentPosition() === BlackLinePosition.MIDDLE) {
            this.currentPosition.set(BlackLinePosition.BOTTOM);
        }
    }

    onSave(): void {
        const content = this.getCurrentContent();
        this.save.emit(content);
    }

    updateLastFocusedEditor(editor: 'above' | 'below'): void {
        this.lastFocusedEditor.set(editor);
    }

    /*
    Intentionally we listen and show toast only for the aboveBL, because this is the important editor
    */
    onAboveCodoxFailed(): void {
        this.store.dispatch(
            ApplicationActions.showToastMessage({
                message: this.i18n()[I18nKeys.PSTWebCodoxInitFailedMessage],
                severity: AlertSeverity.WARNING,
            })
        );
    }

    onCollabUsersChanged(data: { id: string }[]): void {
        this.collabUsers.emit(data);

        const lockUserExists = data.some(codoxUser => {
            return codoxUser.id === this.story().EditLockedBy;
        });
        if (!lockUserExists) {
            this.lockStory.emit(true);
        }
    }

    // Delegates a production command from the toolbar to whichever editor was last focused.
    onToolbarProductionCommand(commandType: ProductionCommandType): void {
        if (this.lastFocusedEditor() === 'above') {
            this.editorComponentAboveBL()?.onProductionCommandClicked(commandType);
        } else {
            this.editorComponentBelowBL()?.onProductionCommandClicked(commandType);
        }
    }

    expandAllElements() {
        const contentAbove = this.aboveContent().replaceAll('expand_less', 'expand_more').replaceAll('--hidden', '');
        const contentBelow = this.belowContent().replaceAll('expand_less', 'expand_more').replaceAll('--hidden', '');
        this.editorAboveBL()?.html.set(contentAbove || '');
        this.editorBelowBL()?.html.set(contentBelow || '');
    }
}
